/**
 * PowerPoint Export Utility (TASK-2026-062)
 *
 * Generates branded PowerPoint presentations from simulator data.
 * Uses pptxgenjs for slide generation.
 */

import PptxGenJS from 'pptxgenjs';

export interface PPTExportData {
  clientName: string;
  generatedAt: Date;
  market?: {
    totalValue: number;
    currency: string;
    growthRate: number;
    topBrands: Array<{ name: string; share: number }>;
  };
  portfolio?: {
    skuCount: number;
    hhi: number;
  };
  opportunities?: Array<{
    type: string;
    title: string;
    impact: string;
    priority: 'high' | 'medium' | 'low';
    recommendation: string;
  }>;
  insights?: Array<{
    category: string;
    insight: string;
  }>;
}

export interface PPTExportOptions {
  filename?: string;
  brandColor?: string;
  includeMarketSlide?: boolean;
  includeOpportunitiesSlide?: boolean;
  includeInsightsSlide?: boolean;
}

// NextMinder brand colors
const BRAND_COLORS = {
  primary: '683BCE',      // Purple
  secondary: '211048',    // Deep Purple
  background: '0E060E',   // Near Black
  surface: '1A1A2E',      // Dark surface
  text: 'FFFFFF',         // White
  textSecondary: '9CA3AF', // Gray
  success: '10B981',      // Green
  warning: 'F59E0B',      // Amber
  danger: 'EF4444',       // Red
};

/**
 * Export simulator data to PowerPoint presentation
 */
export function exportToPowerPoint(
  data: PPTExportData,
  options: PPTExportOptions = {}
): void {
  const {
    filename = `${data.clientName.toLowerCase().replace(/\s+/g, '-')}-presentation`,
    brandColor = BRAND_COLORS.primary,
    includeMarketSlide = true,
    includeOpportunitiesSlide = true,
    includeInsightsSlide = true,
  } = options;

  const pptx = new PptxGenJS();

  // Presentation metadata
  pptx.author = 'NextMinder';
  pptx.title = `${data.clientName} - Market Intelligence`;
  pptx.subject = 'CPG Simulator Export';
  pptx.company = 'NextMinder';

  // Define master slide layout
  pptx.defineSlideMaster({
    title: 'NEXTMINDER_MASTER',
    background: { color: BRAND_COLORS.background },
    objects: [
      // Footer bar
      {
        rect: {
          x: 0,
          y: 5.2,
          w: '100%',
          h: 0.3,
          fill: { color: BRAND_COLORS.surface },
        },
      },
      // Footer text
      {
        text: {
          text: 'CPG Simulator | NextMinder',
          options: {
            x: 0.3,
            y: 5.25,
            w: 5,
            h: 0.2,
            fontSize: 8,
            color: BRAND_COLORS.textSecondary,
          },
        },
      },
      // Page number placeholder
      {
        text: {
          text: 'Slide ',
          options: {
            x: 9,
            y: 5.25,
            w: 0.8,
            h: 0.2,
            fontSize: 8,
            color: BRAND_COLORS.textSecondary,
            align: 'right',
          },
        },
      },
    ],
  });

  // Slide 1: Title Slide
  const titleSlide = pptx.addSlide({ masterName: 'NEXTMINDER_MASTER' });
  titleSlide.addText(data.clientName, {
    x: 0.5,
    y: 1.5,
    w: 9,
    h: 1,
    fontSize: 44,
    fontFace: 'Arial',
    bold: true,
    color: BRAND_COLORS.text,
  });
  titleSlide.addText('Market Intelligence Summary', {
    x: 0.5,
    y: 2.5,
    w: 9,
    h: 0.5,
    fontSize: 24,
    fontFace: 'Arial',
    color: brandColor,
  });
  titleSlide.addText(`Generated: ${data.generatedAt.toLocaleDateString()}`, {
    x: 0.5,
    y: 4,
    w: 9,
    h: 0.3,
    fontSize: 12,
    color: BRAND_COLORS.textSecondary,
  });

  // Slide 2: KPI Dashboard
  const kpiSlide = pptx.addSlide({ masterName: 'NEXTMINDER_MASTER' });
  kpiSlide.addText('Key Performance Indicators', {
    x: 0.5,
    y: 0.3,
    w: 9,
    h: 0.5,
    fontSize: 28,
    fontFace: 'Arial',
    bold: true,
    color: BRAND_COLORS.text,
  });

  // KPI Cards
  const kpiData = [
    {
      label: 'Market Value',
      value: data.market
        ? `${data.market.currency} ${formatLargeNumber(data.market.totalValue)}`
        : 'N/A',
    },
    {
      label: 'Growth Rate',
      value: data.market ? `${(data.market.growthRate * 100).toFixed(1)}%` : 'N/A',
      color: data.market && data.market.growthRate >= 0 ? BRAND_COLORS.success : BRAND_COLORS.danger,
    },
    {
      label: 'Active SKUs',
      value: data.portfolio?.skuCount?.toString() || 'N/A',
    },
    {
      label: 'Portfolio Share',
      value: data.portfolio ? `${(data.portfolio.hhi * 100).toFixed(1)}%` : 'N/A',
    },
  ];

  kpiData.forEach((kpi, index) => {
    const col = index % 2;
    const row = Math.floor(index / 2);
    const x = 0.5 + col * 4.5;
    const y = 1.2 + row * 1.8;

    // Card background
    kpiSlide.addShape(pptx.ShapeType.roundRect, {
      x,
      y,
      w: 4,
      h: 1.5,
      fill: { color: BRAND_COLORS.surface },
      line: { color: brandColor, width: 1 },
    });

    // Label
    kpiSlide.addText(kpi.label, {
      x: x + 0.2,
      y: y + 0.2,
      w: 3.6,
      h: 0.3,
      fontSize: 12,
      color: BRAND_COLORS.textSecondary,
    });

    // Value
    kpiSlide.addText(kpi.value, {
      x: x + 0.2,
      y: y + 0.6,
      w: 3.6,
      h: 0.6,
      fontSize: 28,
      bold: true,
      color: kpi.color || BRAND_COLORS.text,
    });
  });

  // Slide 3: Market Share (if data available)
  if (includeMarketSlide && data.market?.topBrands && data.market.topBrands.length > 0) {
    const marketSlide = pptx.addSlide({ masterName: 'NEXTMINDER_MASTER' });
    marketSlide.addText('Market Share by Brand', {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.5,
      fontSize: 28,
      fontFace: 'Arial',
      bold: true,
      color: BRAND_COLORS.text,
    });

    // Brand share table
    const tableData: PptxGenJS.TableRow[] = [
      [
        { text: 'Brand', options: { bold: true, fill: { color: brandColor }, color: 'FFFFFF' } },
        { text: 'Share', options: { bold: true, fill: { color: brandColor }, color: 'FFFFFF' } },
      ],
      ...data.market.topBrands.slice(0, 10).map(brand => [
        { text: brand.name, options: { color: BRAND_COLORS.text } },
        { text: `${(brand.share * 100).toFixed(1)}%`, options: { color: BRAND_COLORS.text } },
      ]),
    ];

    marketSlide.addTable(tableData, {
      x: 0.5,
      y: 1,
      w: 5,
      colW: [3.5, 1.5],
      border: { type: 'solid', color: BRAND_COLORS.surface, pt: 1 },
      fill: { color: BRAND_COLORS.background },
      fontFace: 'Arial',
      fontSize: 11,
    });
  }

  // Slide 4: Opportunities (if data available)
  if (includeOpportunitiesSlide && data.opportunities && data.opportunities.length > 0) {
    const oppsSlide = pptx.addSlide({ masterName: 'NEXTMINDER_MASTER' });
    oppsSlide.addText('Key Opportunities', {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.5,
      fontSize: 28,
      fontFace: 'Arial',
      bold: true,
      color: BRAND_COLORS.text,
    });

    data.opportunities.slice(0, 4).forEach((opp, index) => {
      const y = 1 + index * 1;
      const priorityColor =
        opp.priority === 'high'
          ? BRAND_COLORS.danger
          : opp.priority === 'medium'
          ? BRAND_COLORS.warning
          : BRAND_COLORS.textSecondary;

      // Opportunity card
      oppsSlide.addShape(pptx.ShapeType.roundRect, {
        x: 0.5,
        y,
        w: 9,
        h: 0.9,
        fill: { color: BRAND_COLORS.surface },
        line: { color: priorityColor, width: 2 },
      });

      // Title
      oppsSlide.addText(opp.title, {
        x: 0.7,
        y: y + 0.1,
        w: 7,
        h: 0.3,
        fontSize: 14,
        bold: true,
        color: BRAND_COLORS.text,
      });

      // Impact
      oppsSlide.addText(opp.impact, {
        x: 0.7,
        y: y + 0.45,
        w: 7,
        h: 0.3,
        fontSize: 11,
        color: BRAND_COLORS.textSecondary,
      });

      // Priority badge
      oppsSlide.addText(opp.priority.toUpperCase(), {
        x: 8,
        y: y + 0.25,
        w: 1.2,
        h: 0.4,
        fontSize: 9,
        bold: true,
        align: 'center',
        color: 'FFFFFF',
        fill: { color: priorityColor },
      });
    });
  }

  // Slide 5: Insights (if data available)
  if (includeInsightsSlide && data.insights && data.insights.length > 0) {
    const insightsSlide = pptx.addSlide({ masterName: 'NEXTMINDER_MASTER' });
    insightsSlide.addText('Strategic Insights', {
      x: 0.5,
      y: 0.3,
      w: 9,
      h: 0.5,
      fontSize: 28,
      fontFace: 'Arial',
      bold: true,
      color: BRAND_COLORS.text,
    });

    data.insights.slice(0, 5).forEach((insight, index) => {
      const y = 1 + index * 0.8;

      // Category badge
      insightsSlide.addText(insight.category, {
        x: 0.5,
        y,
        w: 1.5,
        h: 0.4,
        fontSize: 10,
        bold: true,
        align: 'center',
        color: brandColor,
        fill: { color: BRAND_COLORS.surface },
      });

      // Insight text
      insightsSlide.addText(insight.insight, {
        x: 2.2,
        y,
        w: 7.3,
        h: 0.6,
        fontSize: 12,
        color: BRAND_COLORS.text,
        valign: 'top',
      });
    });
  }

  // Generate and download
  pptx.writeFile({ fileName: `${filename}.pptx` });
}

/**
 * Format large numbers for display
 */
function formatLargeNumber(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toFixed(0);
}
